import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useActionSettings from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';

const ActionsComputerUse = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const [instructions, setInstructions] = useState('');
  const [tag, setTag] = useState('');
  const [processName, setProcessName] = useState('');
  const [maxIterations, setMaxIterations] = useState(10);
  const [allowForegroundInputFallback, setAllowForegroundInputFallback] = useState(false);

  const instructionsRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'computeruse',
    onSaveStatusChange,
    customComparison: (initial) => (
      instructions === (initial.instructions || '') &&
      tag === (initial.tag || '') &&
      processName === (initial.processName || '') &&
      maxIterations === (initial.maxIterations || 10) &&
      allowForegroundInputFallback === Boolean(initial.allowForegroundInputFallback)
    ),
    customSaveData: () => ({
      tag,
      processName,
      instructions,
      maxIterations,
      allowForegroundInputFallback
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          console.error('Failed to parse computer use action data:', error);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      if (parsedData.tag !== undefined) setTag(parsedData.tag);
      if (parsedData.processName !== undefined) setProcessName(parsedData.processName);
      if (parsedData.instructions !== undefined) setInstructions(parsedData.instructions);
      if (parsedData.maxIterations !== undefined) setMaxIterations(parsedData.maxIterations);
      if (parsedData.allowForegroundInputFallback !== undefined) setAllowForegroundInputFallback(Boolean(parsedData.allowForegroundInputFallback));
    }
  });

  const handleSave = () => {
    baseHandleSave({
      tag,
      processName,
      instructions,
      maxIterations,
      allowForegroundInputFallback,
      useConditions: true,
      conditions
    });
  };

  useEffect(() => {
    updateSaveDisabledState();
  }, [instructions, tag, processName, maxIterations, allowForegroundInputFallback, updateSaveDisabledState]);

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTagSelect = (value) => {
    const tagToInsert = `{${value}}`;
    if (menuType === 'tag') {
      setTag((prev) => prev + tagToInsert);
    } else if (menuType === 'processName') {
      setProcessName((prev) => prev + tagToInsert);
    } else if (menuType === 'instructions') {
      instructionsRef.current?.insertTag(value);
    }
    handleMenuClose();
  };

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('ActionsComputerUse.title', 'Application Use Automation')}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      primaryColor="#5757b0"
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      tagsDropdownProps={{
        tags,
        anchorEl,
        handleTagSelect,
        setAnchorEl: handleMenuClose,
        menuType
      }}
    >
      <Grid item xs={12}>
        <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
          <CardHeader
            title={
              <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.25rem' }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: { xs: 1, sm: 1.5 }, py: { xs: 0.5, sm: 0.7 }, mr: { xs: 1, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.85rem' }, letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                  STORE
                </Box>
                {t('computer_use.tag_label', 'Tag to store the application result')}
              </Typography>
            }
            sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', p: { xs: 1.5, sm: 2.5 } }}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <TagEditor
              caseid={caseid}
              value={tag}
              onChange={setTag}
              placeholder={t('computer_use.tag_placeholder', 'Enter a tag name where the application use result will be stored.')}
              singleLine
              onTagClick={(e) => handleMenuOpen(e, 'tag')}
              tags={tags}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
          <CardHeader
            title={
              <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.25rem' }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: { xs: 1, sm: 1.5 }, py: { xs: 0.5, sm: 0.7 }, mr: { xs: 1, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.85rem' }, letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                  APP
                </Box>
                {t('computer_use.process_name_label', 'Target process name')}
              </Typography>
            }
            sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', p: { xs: 1.5, sm: 2.5 } }}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <TagEditor
              caseid={caseid}
              value={processName}
              onChange={setProcessName}
              placeholder={t('computer_use.process_name_placeholder', 'Enter the process name, e.g. notepad.exe')}
              singleLine
              onTagClick={(e) => handleMenuOpen(e, 'processName')}
              tags={tags}
            />
            <Typography variant="body2" sx={{ color: '#666', mt: 1.5 }}>
              {t('computer_use.process_name_help', 'The Windows application must already be open. Use the process executable name, such as notepad.exe or winword.exe.')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
          <CardHeader
            title={
              <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.25rem' }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: { xs: 1, sm: 1.5 }, py: { xs: 0.5, sm: 0.7 }, mr: { xs: 1, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.85rem' }, letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                  DO
                </Box>
                {t('computer_use.instructions_label', 'Instructions for Application Navigation')}
              </Typography>
            }
            sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', p: { xs: 1.5, sm: 2.5 } }}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <TagEditor
              caseid={caseid}
              ref={instructionsRef}
              value={instructions}
              onChange={setInstructions}
              placeholder={t('computer_use.instructions_placeholder', "Describe what to do in the application, e.g. 'Open the File menu and read the current document title'. You can use tags.")}
              minHeight="150px"
              onTagClick={(e) => handleMenuOpen(e, 'instructions')}
              tags={tags}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
          <CardHeader
            title={
              <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.25rem' }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: { xs: 1, sm: 1.5 }, py: { xs: 0.5, sm: 0.7 }, mr: { xs: 1, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.85rem' }, letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                  MODE
                </Box>
                {t('computer_use.foreground_fallback_label', 'Allow foreground input fallback')}
              </Typography>
            }
            sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', p: { xs: 1.5, sm: 2.5 } }}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={allowForegroundInputFallback}
                  onChange={(e) => setAllowForegroundInputFallback(e.target.checked)}
                />
              )}
              label={t('computer_use.foreground_fallback_switch', 'Use real mouse/keyboard only if UI automation fails')}
            />
            <Typography variant="body2" sx={{ color: '#666', mt: 1.5 }}>
              {t('computer_use.foreground_fallback_help', 'When enabled, the agent first tries non-invasive UI automation, then may focus the window, move the mouse, click, or type if the application does not respond to UIA/Win32 actions alone.')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          type="number"
          label={t('action_common.max_iterations')}
          value={maxIterations}
          onChange={(e) => setMaxIterations(parseInt(e.target.value, 10) || 10)}
          variant="outlined"
          helperText={t('computer_use.max_iterations_help', t('action_common.max_iterations_help'))}
          InputProps={{ inputProps: { min: 1, max: 100 } }}
        />
      </Grid>
    </ActionSettingsLayout>
  );
};

export default ActionsComputerUse;
